import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import styles from './Modal.module.css';
import { createCase } from '../../../services/clienteService';
import { ClienteCaso, NewCaseFormData } from '../../../interfaces/cliente.types';

interface AddCasoModalProps {
    onClose: () => void;
    onCaseCreated: (newCase: ClienteCaso) => void;
}

export const AddCasoModal: React.FC<AddCasoModalProps> = ({ onClose, onCaseCreated }) => {
    const [formData, setFormData] = useState<Partial<NewCaseFormData>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.nombre_completo || !formData.tipo_caso) {
            setError('El Nombre del Cliente y el Tipo de Caso son obligatorios.');
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const newCase = await createCase(formData as NewCaseFormData);
            onCaseCreated(newCase);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const modalVariants = {
        hidden: { y: 50, opacity: 0 },
        visible: { y: 0, opacity: 1 },
        exit: { y: 50, opacity: 0 }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <motion.div 
                className={styles.modalContent}
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                onClick={(e) => e.stopPropagation()}
            >
                <header className={styles.modalHeader}>
                    <h2>Registrar Nuevo Caso</h2>
                    <button onClick={onClose} className={styles.closeButton}><X size={24} /></button>
                </header>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label htmlFor="nombre_completo">Nombre Completo del Cliente*</label>
                            <input type="text" id="nombre_completo" name="nombre_completo" onChange={handleChange} required />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="tipo_caso">Tipo de Caso*</label>
                            <input type="text" id="tipo_caso" name="tipo_caso" onChange={handleChange} required />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="numero_expediente">N° de Expediente</label>
                            <input type="text" id="numero_expediente" name="numero_expediente" onChange={handleChange} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="dni">DNI</label>
                            <input type="text" id="dni" name="dni" onChange={handleChange} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="telefono">Teléfono</label>
                            <input type="text" id="telefono" name="telefono" onChange={handleChange} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="email">Email</label>
                            <input type="email" id="email" name="email" onChange={handleChange} />
                        </div>
                        <div className={styles.formGroup}>
                             <label htmlFor="monto_total_acordado">Monto Acordado (S/)</label>
                            <input type="number" step="0.01" id="monto_total_acordado" name="monto_total_acordado" onChange={handleChange} placeholder="0.00" />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="lugar">Lugar / Sede</label>
                            <input type="text" id="lugar" name="lugar" onChange={handleChange} />
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label htmlFor="descripcion_caso">Descripción del Caso</label>
                            <textarea id="descripcion_caso" name="descripcion_caso" rows={4} onChange={handleChange}></textarea>
                        </div>
                    </div>
                    {error && <p className={styles.errorMessage}>{error}</p>}
                    <footer className={styles.modalFooter}>
                        <button type="button" className={styles.cancelButton} onClick={onClose} disabled={isLoading}>Cancelar</button>
                        <button type="submit" className={styles.submitButton} disabled={isLoading}>
                            {isLoading ? 'Guardando...' : 'Guardar Caso'}
                        </button>
                    </footer>
                </form>
            </motion.div>
        </div>
    );
};